import cn from "classnames";
import dayjs from "dayjs";

import { useEffect, useMemo, useState } from "react";

import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import FlightClassOutlinedIcon from "@mui/icons-material/FlightClassOutlined";
import FlightLandOutlinedIcon from "@mui/icons-material/FlightLandOutlined";
import FlightTakeoffOutlinedIcon from "@mui/icons-material/FlightTakeoffOutlined";
import SearchIcon from "@mui/icons-material/Search";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import { LoadingButton } from "@mui/lab";
import {
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Fab,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  IconButton,
  SvgIcon,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";

import ProChip from "@/components/ProChip";
import SelectPassengerV2 from "@/components/SelectPassengerV2";
import { FlightWayEnum, cabinOptionMap, flightWayOptions, passengerTypeOptions } from "@/constants";
import { megaProgramsOptions } from "@/constants/mega";
import {
  FREE_MEGA_PROGRAMS,
  MAX_MEGA_FLIGHT_RANGE,
  MAX_MEGA_PROGRAMS,
} from "@/constants/membership";
import { useGlobalStoreContext } from "@/context/globalStoreContext";
import useSearchCondition, { SearchConditionType } from "@/hooks/useMegaSearchCondition";
import useMegaSearchLimit from "@/hooks/useMegaSearchLimit";
import usePlatform from "@/hooks/usePlatform";

import SearchAirport from "./SearchAirport";
import SearchDate from "./SearchDate";
import SearchRangeDate from "./SearchRangeDate";
import SearchSelect from "./SearchSelect";
import SearchSwitchMega from "./SearchSwitchMega";

const flightWayIconMap: Record<string, typeof SvgIcon> = {
  [FlightWayEnum.OneWay]: ArrowRightAltIcon,
  [FlightWayEnum.RoundTrip]: SyncAltIcon,
};

interface SearchBarProps {
  isLoading?: boolean;
  withSearchBtn?: boolean;
  withMobileSearchBtn?: boolean;
  onSearch?: (data: SearchConditionType) => void;
  onChange?: (data: SearchConditionType) => void;
}

const promot_style = { fontWeight: "bold" };
const SearchBar = ({
  onChange,
  onSearch,
  withSearchBtn = false,
  withMobileSearchBtn = false,
  isLoading = false,
}: SearchBarProps) => {
  const { isMobile } = usePlatform();
  const [showProgramsTips, setShowProgramsTips] = useState(false);

  const {
    searchLimit,
    condition,
    setCondition,
    onLocationSwapChange,
    onSwitchChange,
    onOneWayDateChange,
    onOneWayRangeDateChange,
    onRoundTripDepartureDateChange,
    onRoundTripReturnDateChange,
    onDepartureRangeChange,
    onReturnRangeChange,
    onProgramsChange,
  } = useSearchCondition();

  const store = useGlobalStoreContext();

  const { data: megaSearchLimit } = useMegaSearchLimit();
  const [searchLimitModalVisible, setSearchLimitModalVisible] = useState(false);
  const disableSwap = useMemo(() => {
    return !condition.fromLocation.length || !condition.toLocation.length;
  }, [condition.fromLocation, condition.toLocation]);

  const roundTrip = useMemo(
    () => condition.flightWay === FlightWayEnum.RoundTrip,
    [condition.flightWay]
  );

  useEffect(() => {
    onChange && onChange(condition);
  }, [condition, onChange]);

  const isBigScreen = useMediaQuery("(min-width:1200px)");

  const airportSmGridItem = useMemo(() => {
    if (roundTrip) {
      return withSearchBtn && isBigScreen ? 6 : 7.5;
    } else {
      return withSearchBtn && isBigScreen ? 8 : 7.5;
    }
  }, [roundTrip, withSearchBtn, isBigScreen]);

  const dateSmGridItem = 12 - airportSmGridItem;
  const roundTripDateSmGridItem = withSearchBtn && isBigScreen ? 6 : 12;
  const roundTripRange = !(searchLimit.daterange_dep <= 1 && searchLimit.daterange_arrival <= 1);

  const sortProgramOptions = useMemo(() => {
    return megaProgramsOptions.sort((a, b) => {
      if (condition.programs?.includes(a.value) && !condition.programs?.includes(b.value)) {
        return -1;
      }
      if (!condition.programs?.includes(a.value) && condition.programs?.includes(b.value)) {
        return 1;
      }

      return a.value.localeCompare(b.value);
    });
  }, [condition.programs]);

  const saveProgramsLocal = (data: any[]) =>
    localStorage.setItem("__FlightSearchPrograms__", JSON.stringify(data || []));
  return (
    <div>
      <div className="relative flex items-center justify-start w-full py-3 overflow-hidden">
        <SearchSelect
          value={condition.flightWay}
          options={flightWayOptions}
          Icon={flightWayIconMap[condition.flightWay]}
          onChange={(data) => {
            setCondition({ ...condition, flightWay: data });
          }}
        />

        <SelectPassengerV2
          min={passengerTypeOptions[0].value}
          max={passengerTypeOptions[passengerTypeOptions.length - 1].value}
          value={condition.pax}
          onChange={(data) => {
            setCondition({ ...condition, pax: data });
          }}
          childValue={condition.children}
          onChangeChild={(data) => {
            setCondition({ ...condition, children: data });
          }}
        />

        <SearchSelect
          value={condition.cabins}
          options={cabinOptionMap}
          Icon={isMobile ? null : FlightClassOutlinedIcon}
          onChange={(data) => {
            setCondition({ ...condition, cabins: data });
          }}
        />

        {!isMobile ? (
          <div className="flex justify-end flex-1 items-center">
            <SearchSwitchMega
              showUpsell={store?.show_upsell}
              value={condition.rangeV2}
              onChange={onSwitchChange}
            />
            {store.show_upsell && megaSearchLimit && (
              <Typography color="textPrimary" className="!ml-[6px]">
                ({megaSearchLimit?.api_count}/{megaSearchLimit?.api_limit} today)
              </Typography>
            )}
          </div>
        ) : null}
      </div>

      <Grid
        container
        rowSpacing={2}
        columnSpacing={1}
        className="relative"
        sx={{ paddingRight: withSearchBtn ? "55px" : 0 }}
      >
        <Grid item xs={12} sm={airportSmGridItem}>
          <Grid container alignItems="center" rowGap={2}>
            <Grid item xs={12} sm={5.6}>
              <SearchAirport
                placeholder="Where from?"
                Icon={<FlightTakeoffOutlinedIcon />}
                selectData={condition.fromLocation}
                onChange={(data) => {
                  setCondition({ ...condition, fromLocation: data });
                }}
                maxCount={searchLimit.choice_count_dep}
              />
            </Grid>

            {!isMobile ? (
              <Grid item xs={1} sm={0.8} alignItems="center">
                <div className="flex items-center justify-center">
                  <IconButton
                    aria-label="swap"
                    size="small"
                    onClick={onLocationSwapChange}
                    disabled={disableSwap}
                  >
                    <SwapHorizIcon />
                  </IconButton>
                </div>
              </Grid>
            ) : (
              <div className={cn("absolute right-6 z-10", { hidden: disableSwap })}>
                <Fab color="primary" aria-label="swap" size="small" onClick={onLocationSwapChange}>
                  <SwapHorizIcon className="rotate-90" />
                </Fab>
              </div>
            )}

            <Grid item xs={12} sm={5.6}>
              <SearchAirport
                placeholder="Where to?"
                Icon={<FlightLandOutlinedIcon />}
                selectData={condition.toLocation}
                onChange={(data) => {
                  setCondition({ ...condition, toLocation: data });
                }}
                maxCount={searchLimit.choice_count_arrival}
              />
            </Grid>
          </Grid>
        </Grid>

        {!roundTrip ? (
          <Grid item xs={12} sm={dateSmGridItem}>
            <div className="flex">
              {searchLimit.daterange_dep > 1 ? (
                <SearchRangeDate
                  key="oneWayRangeDate"
                  label="Departure Date Range"
                  calendarRangeDays={store?.show_upsell ? searchLimit.search_calendar_range : 365}
                  maxRangeDays={searchLimit.daterange_dep}
                  defaultValue={[
                    condition.oneWayRangeStartDate ? dayjs(condition.oneWayRangeStartDate) : null,
                    condition.oneWayRangeEndDate ? dayjs(condition.oneWayRangeEndDate) : null,
                  ]}
                  onChange={onOneWayRangeDateChange}
                  customToolbar={
                    <Typography
                      sx={promot_style}
                      className="pt-[15px] px-[24px] break-words"
                      color="primary"
                    >
                      {"You can search up to " + searchLimit.daterange_dep + " days"}
                    </Typography>
                  }
                />
              ) : (
                <SearchDate
                  key="oneWayDate"
                  label="Departure Date"
                  defaultValue={condition.oneWayDate ? dayjs(condition.oneWayDate) : undefined}
                  calendarRangeDays={store?.show_upsell ? searchLimit.search_calendar_range : 365}
                  onChange={onOneWayDateChange}
                  customToolbar={
                    <Typography
                      sx={promot_style}
                      className="pt-[15px] px-[24px] break-words"
                      color="primary"
                    >
                      {"You can search up to " + searchLimit.daterange_dep + " days"}
                    </Typography>
                  }
                />
              )}
            </div>
          </Grid>
        ) : null}

        {roundTrip && !roundTripRange ? (
          <Grid item xs={12} sm={dateSmGridItem}>
            <Grid container alignItems="center" rowGap={2} columnSpacing={1}>
              <Grid item xs={12} sm={roundTripDateSmGridItem}>
                <SearchDate
                  key="departureDate"
                  label="Departure Date"
                  defaultValue={
                    condition.roundTripDepartureDate
                      ? dayjs(condition.roundTripDepartureDate)
                      : undefined
                  }
                  onChange={onRoundTripDepartureDateChange}
                  customToolbar={
                    <Typography
                      sx={promot_style}
                      className="pt-[15px]  px-[24px] break-words"
                      color="primary"
                    >
                      {"You can search up to " + searchLimit.daterange_dep + " days"}
                    </Typography>
                  }
                />
              </Grid>

              <Grid item xs={12} sm={roundTripDateSmGridItem}>
                <SearchDate
                  key="returnDate"
                  label="Return Date"
                  defaultValue={
                    condition.roundTripReturnDate ? dayjs(condition.roundTripReturnDate) : undefined
                  }
                  minDate={
                    condition.roundTripDepartureDate
                      ? dayjs(condition.roundTripDepartureDate)
                      : undefined
                  }
                  onChange={onRoundTripReturnDateChange}
                  customToolbar={
                    <Typography
                      sx={promot_style}
                      className="pt-[15px]  px-[24px] break-words"
                      color="primary"
                    >
                      {"You can search up to " + searchLimit.daterange_arrival + " days"}
                    </Typography>
                  }
                />
              </Grid>
            </Grid>
          </Grid>
        ) : null}

        {roundTrip && roundTripRange ? (
          <Grid item xs={12} sm={dateSmGridItem}>
            <Grid container alignItems="center" rowGap={2} columnSpacing={1}>
              <Grid item xs={12} sm={roundTripDateSmGridItem}>
                <SearchRangeDate
                  key="departureRangeDate"
                  defaultValue={[
                    condition.departureRangeStartDate
                      ? dayjs(condition.departureRangeStartDate)
                      : null,
                    condition.departureRangeEndDate ? dayjs(condition.departureRangeEndDate) : null,
                  ]}
                  // maxRangeDays={7}
                  calendarRangeDays={store?.show_upsell ? searchLimit.search_calendar_range : 365}
                  maxRangeDays={searchLimit.daterange_dep}
                  label="Departure Date Range"
                  onChange={onDepartureRangeChange}
                  customToolbar={
                    <Typography
                      sx={promot_style}
                      className="pt-[15px]  px-[24px] break-words"
                      color="primary"
                    >
                      {"You can search up to " + searchLimit.daterange_dep + " days"}
                    </Typography>
                  }
                />
              </Grid>

              <Grid item xs={12} sm={roundTripDateSmGridItem}>
                <SearchRangeDate
                  key="returnRangeDate"
                  defaultValue={[
                    condition.returnRangeStartDate ? dayjs(condition.returnRangeStartDate) : null,
                    condition.returnRangeEndDate ? dayjs(condition.returnRangeEndDate) : null,
                  ]}
                  calendarRangeDays={store?.show_upsell ? searchLimit.search_calendar_range : 365}
                  maxRangeDays={searchLimit.daterange_arrival}
                  label="Return Date Range"
                  onChange={onReturnRangeChange}
                  minDate={
                    condition.departureRangeStartDate
                      ? dayjs(condition.departureRangeStartDate)
                      : undefined
                  }
                  customToolbar={
                    <Typography
                      sx={promot_style}
                      className="pt-[15px]  px-[24px] break-words"
                      color="primary"
                    >
                      {"You can search up to " + searchLimit.daterange_arrival + " days"}
                    </Typography>
                  }
                />
              </Grid>
            </Grid>
          </Grid>
        ) : null}

        {withSearchBtn ? (
          <div className="flex items-center justify-end mt-[15px] ml-[10px] absolute right-0">
            <LoadingButton
              color="primary"
              loading={isLoading}
              variant="contained"
              className="!min-w-[48px] !h-[56px] !w-[48px]"
              onClick={() => {
                if (megaSearchLimit?.disabled && store.show_upsell) {
                  setSearchLimitModalVisible(true);
                  return;
                }
                onSearch && onSearch(condition);
              }}
            >
              <SearchIcon sx={{ fontSize: "24px" }} />
            </LoadingButton>
          </div>
        ) : null}
      </Grid>

      <div className="mt-2">
        <FormControl component="fieldset" variant="standard">
          <FormGroup row>
            {sortProgramOptions.map((item) => {
              return (
                <FormControlLabel
                  key={item.name}
                  control={
                    <Checkbox
                      value={item.value}
                      onChange={(e) => {
                        const programs = condition.programs || [];

                        if (programs.includes(item.value)) {
                          saveProgramsLocal(programs.filter((v) => v !== e.target.value));
                          onProgramsChange(programs.filter((v) => v !== e.target.value));
                        } else {
                          const newValue = [...programs, e.target.value];

                          const maxNum = store.show_upsell ? FREE_MEGA_PROGRAMS : MAX_MEGA_PROGRAMS;
                          if (newValue.length > maxNum) {
                            setShowProgramsTips(true);
                            return;
                          }
                          saveProgramsLocal(newValue);
                          onProgramsChange(newValue);
                        }
                      }}
                      checked={condition.programs?.includes(item.value)}
                    />
                  }
                  label={
                    <Tooltip title={item.fullname}>
                      <Typography component="span" color="textPrimary">
                        {item.name}
                      </Typography>
                    </Tooltip>
                  }
                />
              );
            })}
          </FormGroup>
        </FormControl>
      </div>
      {/* {isMobile ? (
        <div className="pt-2">
          <SearchSwitchMega
            showUpsell={store?.show_upsell}
            value={condition.rangeV2}
            onChange={onSwitchChange}
          />
        </div>
      ) : null} */}

      {withMobileSearchBtn ? (
        <div className="pb-[18px] pt-[10px]">
          <LoadingButton
            variant="contained"
            className="w-full"
            loading={isLoading}
            startIcon={<SearchIcon />}
            onClick={() => {
              if (megaSearchLimit?.disabled && store.show_upsell) {
                setSearchLimitModalVisible(true);
                return;
              }
              onSearch && onSearch(condition);
            }}
          >
            Search
          </LoadingButton>
        </div>
      ) : null}

      <Dialog
        open={showProgramsTips}
        onClose={() => setShowProgramsTips(false)}
        PaperProps={{
          sx: {
            borderRadius: "12px",
          },
        }}
      >
        <DialogContent>
          {store.show_upsell ? (
            <div>
              <Typography color="primary" fontSize={18} fontWeight={600}>
                PRO feature
              </Typography>
              <Typography fontSize={16}>
                {"Upgrade to"}
                <ProChip />
                {" to search up to " +
                  MAX_MEGA_PROGRAMS +
                  " programs with " +
                  MAX_MEGA_FLIGHT_RANGE +
                  "-entry at one query."}
              </Typography>
            </div>
          ) : (
            <div>
              <Typography color="primary" fontSize={16}>
                PRO
              </Typography>
              <Typography fontSize={16}>
                {"Search " +
                  MAX_MEGA_PROGRAMS +
                  " programs at one query with " +
                  MAX_MEGA_FLIGHT_RANGE +
                  "-entry mega search."}
              </Typography>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowProgramsTips(false)}>OK</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={searchLimitModalVisible}
        onClose={() => setSearchLimitModalVisible(false)}
        PaperProps={{
          sx: {
            borderRadius: "12px",
          },
        }}
      >
        <DialogContent>
          <Typography color="primary" fontSize={18} fontWeight={600}>
            PRO feature
          </Typography>
          <Typography fontSize={16}>
            Upgrade to PRO to perform unlimited mega search up to 5 programs with 30-entry at one
            query.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSearchLimitModalVisible(false)}>OK</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SearchBar;
